import React, { useEffect, useMemo, useState } from 'react';
import {
  KendoReactGrid,
  GridColumn,
  GridCustomFilterCellProps
} from '@Shared.Angular/kendo.react/KendoReactGrid';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor
} from '@progress/kendo-data-query';
import AutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/AutoCompleteFilter';
import ActionButtons from '../ActionButtons';
import {
  DeleteEntityTypes,
  FlowinglyPermissions,
  FlowModelPublishType
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import { LibraryTagsModal } from '../runner.library.tags/LibraryTagsModal';
import { HeaderTdElement } from '@progress/kendo-react-data-tools';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import StatusBadge from '../StatusBadge';
import {
  IFlowModelSummary,
  IFlowModelTagSummary
} from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelSummary';
import IFlowModelForUser from '@Shared.Angular/@types/core/contracts/queryModel/flows/flowModelForUser';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';
import { DateFilter } from '@Shared.Angular/flowingly.components/Filters/DateFilter/DateFilter';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';
import { RunnerSetupDeleteComponentWarningModal } from '@Client/runner.setup/runner.setup.delete/RunnerSetupDeleteComponentWarningModal';
import {
  buildComponentWarningData,
  ComponentTypeName,
  getComponentTypeNameFromPublishType,
  IComponentWarningData
} from '@Client/runner.shared/library/ComponentWarningHelper';

const LibraryProcessMapsGrid = () => {
  const APP_CONFIG = getService('APP_CONFIG');
  const flowModelApiService = getService('flowModelApiService');
  const permissions = getService('permissionsService');
  const notificationService = getService('notificationService');
  const dialogService = getService('dialogService');
  const flowinglyApiService = getService('flowinglyApiService');
  const modelerUrlService = getService('tempModelerUrlService');
  const moment = getService('momentService');
  const pubsubService = getService('pubsubService');
  const categoryApiService = getService('categoryApiService');
  const libraryService = getService('libraryService');

  const pageSize = 15;
  const [allGridItems, setAllGridItems] = useState<ILibraryFlowModelSummary[]>(
    []
  );
  const [gridItemsLoaded, setGridItemsLoaded] = useState(false);
  const [viewTagsTarget, setViewTagsTarget] =
    useState<ILibraryFlowModelSummary>();
  const [usedTagIds, setUsedTagIds] = useState<Guid[]>([]);
  const [tagFilterIds, setTagFilterIds] = useState<Guid[]>([]);
  const [categoryFilterId, setCategoryFilterId] = React.useState<Guid | null>(
    null
  );
  const [componentWarningData, setComponentWarningData] =
    useState<IComponentWarningData | null>(null);

  const canCreateFlowModels = permissions.currentUserHasPermission(
    FlowinglyPermissions.FLOWMODEL_CREATE
  );

  const subscriberId = 'libraryProcessMapsGrid';
  pubsubService.subscribeManyLate(
    [
      'SIGNALR_WORKFLOW_PUBLISHED',
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      'SIGNALR_NEW_FLOW_MODEL_SAVED',
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      'SIGNALR_WORKFLOW_UNPUBLISHED_CHANGES'
    ],
    () => {
      getData();
    },
    subscriberId
  );

  const getData = () => {
    const promises: angular.IPromise<unknown>[] = [
      flowModelApiService.deferedFlowModels.promise,
      flowModelApiService.deferedFlowModelTags.promise
    ];
    Promise.all(promises).then(
      ([flowModels, flowModelTags]: [
        IFlowModelSummary[],
        IFlowModelTagSummary[]
      ]) => {
        const usedTagIdSet = new Set<Guid>();
        const flowModelTagsMap = flowModelTags.reduce((acc, tagSummary) => {
          acc[tagSummary.flowModelId] = tagSummary.tags;
          return acc;
        }, {} as Record<string, Guid[]>);
        const publishedFlowModels = flowModels
          .filter(
            (fm) =>
              fm.publishTypeValue === FlowModelPublishType.PROCESS_MAP ||
              fm.publishTypeValue === FlowModelPublishType.PROCESS_MAP_COMPONENT
          )
          .map(
            (fm) =>
              ({
                ...fm,
                lastUpdatedLocal: new Date(fm.lastEditedDate + 'Z'),
                reviewDateLocal: fm.processReviewDate
                  ? new Date(fm.processReviewDate + 'Z')
                  : undefined,
                tagIds: flowModelTagsMap[fm.id] || []
              } as ILibraryFlowModelSummary)
          );

        publishedFlowModels.forEach((d) => {
          d.tagIds.forEach((tagId) => usedTagIdSet.add(tagId));
        });
        setUsedTagIds(Array.from(usedTagIdSet));

        setAllGridItems(publishedFlowModels);
        setGridItemsLoaded(true);
      }
    );
  };
  useEffect(() => {
    getData();
  }, []);

  const getTagFilter = () => {
    return {
      field: 'tagIds',
      operator: (itemTagIds: Guid[]) => {
        return (
          !tagFilterIds ||
          tagFilterIds.length === 0 ||
          (itemTagIds &&
            tagFilterIds.some((tagId) => itemTagIds.includes(tagId)))
        );
      }
    };
  };

  const getCategoryFilter = () => {
    return {
      field: 'categoryId',
      operator: (itemCategoryId: Guid) => {
        return !categoryFilterId || categoryFilterId === itemCategoryId;
      }
    };
  };

  const [filter, setFilter] = React.useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [getTagFilter()]
  });

  useEffect(() => {
    const newCategoryFilter = filter.filters.find(
      (f: FilterDescriptor) => f.field === 'category'
    ) as FilterDescriptor;
    const validFilters = newCategoryFilter
      ? filter.filters.filter(
          (f: FilterDescriptor) =>
            f.field !== 'categoryId' &&
            (categoryFilterId || f.field !== 'category')
        )
      : filter.filters;
    setFilter({
      ...filter,
      filters: validFilters.map((f: FilterDescriptor) => {
        if (f.field === 'tagIds') {
          return getTagFilter();
        } else if (f.field === 'category') {
          return getCategoryFilter();
        } else {
          return f;
        }
      })
    });
  }, [tagFilterIds, categoryFilterId]);

  const namesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.name))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const nameFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, namesMemo, 'Search by Name'),
    [namesMemo]
  );

  const creatorNamesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.creatorName))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const creatorFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, creatorNamesMemo, 'Search by Creator'),
    [creatorNamesMemo]
  );

  const ownerNamesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.processOwner))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const ownerFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, ownerNamesMemo, 'Search by Process Owner'),
    [ownerNamesMemo]
  );

  const dateFilterCell: React.FC<GridCustomFilterCellProps> = (props) => (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ''}
      {...props.thProps}
    >
      <DateFilter
        value={props.value as Date | null}
        onChange={(args) =>
          props.onChange?.({
            value: args.value,
            operator: args.operator,
            syntheticEvent: args.syntheticEvent
          })
        }
      />
    </HeaderTdElement>
  );

  const typesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.publishType))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const typesFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, typesMemo, 'Search by Type'),
    [typesMemo]
  );

  const categoryFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <HeaderTdElement
        columnId={props.thProps?.columnId || ''}
        {...props.thProps}
      >
        <KendoReactDropDownTree
          categories={categoryApiService.cachedCategories}
          categoryClicked={(id: Guid) => {
            if (id) {
              props.onChange({
                field: 'categoryId',
                operator: 'eq',
                value: id
              } as any);
            } else {
              props.onChange({
                value: null,
                operator: 'neq'
              } as any);
            }
            setCategoryFilterId(id);
          }}
          selectedId={categoryFilterId}
          dataItemKey="id"
          textField="name"
          subItemsField="subCategories"
          defaultItemId={null}
          showFilter={true}
        />
      </HeaderTdElement>
    );
  };

  const statusMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.status))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const statusFilterCell = (props) => {
    return (
      <HeaderTdElement
        columnId={props.thProps?.columnId || ''}
        {...props.thProps}
      >
        <AutoCompleteFilter
          {...props}
          data={statusMemo}
          placeholder="Search by Status"
          onChange={(e) => {
            if (e.value) {
              props.onChange({
                field: 'status',
                operator: 'eq',
                value: e.value
              } as any);
            } else {
              props.onChange({
                value: null,
                operator: ''
              });
            }
          }}
        />
      </HeaderTdElement>
    );
  };

  const initialSort = useMemo(
    () => [{ field: 'lastUpdatedLocal', dir: 'desc' } as SortDescriptor],
    []
  );

  const ellipsisCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    return (
      <td {...props.tdProps} title={value}>
        {value}
      </td>
    );
  };

  const nameCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    const unpublishedMessage = 'This Flow Model has unpublished changes';
    return (
      <td
        {...props.tdProps}
        title={
          value +
          (props.dataItem.hasUnPublishedChanges
            ? ' - ' + unpublishedMessage
            : '')
        }
      >
        {value}
        {props.dataItem.hasUnPublishedChanges && (
          <div className="unpublished-changes-message secondary-text">
            {unpublishedMessage}
          </div>
        )}
      </td>
    );
  };

  const dateCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    if (!value) {
      return <td {...props.tdProps}></td>;
    }
    return (
      <td
        {...props.tdProps}
        title={moment(value).format('YYYY-MM-DD HH:mm:ss')}
      >
        {moment(value).format('DD/MM/YYYY')}
      </td>
    );
  };

  const statusCell = (props: GridCustomCellProps) => {
    return (
      <td {...props.tdProps}>
        <StatusBadge status={props.dataItem.status} />
      </td>
    );
  };

  const openProcessMapView = (dataItem: ILibraryFlowModelSummary) => {
    getService('libraryService').openProcessMap(dataItem.id);
  };

  const openLockModal = (dataItem: ILibraryFlowModelSummary) => {
    dialogService
      .showDialog({
        template:
          'Client/runner.library/runner.library.flow.model.owner/runner.library.flow.model.owner.dialog.tmpl.html',
        controller: 'flowModelOwnerDialogController',
        appendClassName: 'ngdialog-theme-plain w-700',
        data: {
          flowModelId: dataItem.id,
          flowModelName: dataItem.name,
          isPublic: dataItem.publicType !== null,
          publicType: dataItem.publicType,
          isProcessMapType: true
        }
      })
      .then((result) => {
        if (
          dialogService.isCloseModalWithCancelAction(result) ||
          result === null
        ) {
          return;
        }

        dataItem.publicType = result.publicType;
        dataItem.status =
          result.publicType === null
            ? 'Internal'
            : result.publicType === PublicType.Public
            ? 'Public'
            : 'Public SSO';
        const items = allGridItems.map((item) =>
          item.id === dataItem.id ? { ...dataItem } : item
        );
        setAllGridItems(items);
        if (result.message)
          notificationService.showSuccessToast(result.message, 4000);
        else
          notificationService.showSuccessToast(
            `${dataItem.name} updated`,
            4000
          );
      });
  };

  const openModeler = (dataItem: ILibraryFlowModelSummary) => {
    modelerUrlService.openModeler({ flowModelId: dataItem.id });
  };

  const copyFlowModel = (dataItem: ILibraryFlowModelSummary) => {
    modelerUrlService.openModeler({
      flowModelId: dataItem.id,
      cloneModel: true
    });
  };

  const showComponentWarningModal = (
    componentTypeName: ComponentTypeName,
    flowModels: IFlowModelForUser[]
  ): void => {
    setComponentWarningData(
      buildComponentWarningData(componentTypeName, flowModels)
    );
  };

  const closeComponentWarningModal = (): void => {
    setComponentWarningData(null);
  };

  const showDeleteModal = (dataItem: ILibraryFlowModelSummary): void => {
    dialogService
      .showDialog({
        template:
          'Client/runner.setup/runner.setup.delete/runner.setup.delete.dialog.tmpl.html',
        controller: 'setupDeleteDialogController',
        appendClassName: 'ngdialog-normal',
        data: {
          itemToDelete: dataItem,
          entityType: DeleteEntityTypes.flowModelEntityType
        }
      })
      .then(function (response) {
        if (dialogService.isCloseModalWithCancelAction(response)) {
          return;
        }
        flowModelApiService.deleteFlowModel(dataItem.id).then(() => {
          notificationService.showSuccessToast('Flow Model Deleted');
        });
      });
  };

  const openDeleteModal = (dataItem: ILibraryFlowModelSummary): void => {
    const componentTypeName: ComponentTypeName | null =
      getComponentTypeNameFromPublishType(dataItem.publishTypeValue);
    if (!componentTypeName) {
      showDeleteModal(dataItem);
      return;
    }

    flowinglyApiService
      .getFlowModelsReferencingComponent(dataItem.id)
      .then((flowModels: IFlowModelForUser[]) => {
        if (!flowModels || flowModels.length === 0) {
          showDeleteModal(dataItem);
          return;
        }

        showComponentWarningModal(componentTypeName, flowModels);
      })
      .catch(() => {
        notificationService.showErrorToast('Unable to verify component usage.');
      });
  };

  const generateActionsCell = (props: GridCustomCellProps) => {
    const item: ILibraryFlowModelSummary = props.dataItem;
    const viewHandler = () => openProcessMapView(item);
    let flowModelLockHandler: () => void;
    let editHandler: () => void;
    let copyHandler: () => void;
    const tagHandler = APP_CONFIG.enableTagging
      ? () => setViewTagsTarget(item)
      : undefined;
    let deleteHandler: () => void;

    if (item.editableByCurrentUser) {
      flowModelLockHandler = () => openLockModal(item);
      editHandler = () => openModeler(item);
      deleteHandler = () => openDeleteModal(item);
    }
    if (item.editableByCurrentUser && canCreateFlowModels) {
      copyHandler = () => copyFlowModel(item);
    }

    return (
      <td {...props.tdProps}>
        <ActionButtons
          showButtons={{
            flowModelLock: true,
            edit: true,
            copy: true,
            tags: APP_CONFIG.enableTagging,
            delete: true
          }}
          onViewClick={viewHandler}
          onFlowModelLockClick={flowModelLockHandler}
          onEditClick={editHandler}
          onCopyClick={copyHandler}
          onTagsClick={tagHandler}
          onDeleteClick={deleteHandler}
        />
      </td>
    );
  };

  return (
    (!gridItemsLoaded && <div></div>) ||
    (gridItemsLoaded && allGridItems.length > 0 && (
      <div className="library-grid">
        <TagFilter usedTagIds={usedTagIds} onApply={setTagFilterIds} />
        <KendoReactGrid
          id="library-process-maps-grid"
          autoProcessData={{
            filter: true,
            search: true,
            sort: true,
            group: false,
            page: true
          }}
          data={allGridItems}
          dataItemKey="id"
          defaultSort={initialSort}
          defaultTake={pageSize}
          filter={filter}
          filterable={true}
          onFilterChange={(filterChangeEvent) => {
            setFilter(filterChangeEvent.filter);
          }}
          pageable={{
            buttonCount: 10,
            info: true,
            previousNext: true
          }}
          sortable={true}
          resizable={true}
        >
          <GridColumn
            field="name"
            title="Name"
            className="column-name ellipsis-column"
            cells={{ data: nameCell, filterCell: nameFilterCellMemo }}
          />
          <GridColumn
            field="creatorName"
            title="Created By"
            className="column-created-by ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: creatorFilterCellMemo }}
          />
          <GridColumn
            field="processOwner"
            title="Process Owner"
            className="column-process-owner ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: ownerFilterCellMemo }}
          />
          <GridColumn
            field="lastUpdatedLocal"
            title="Last Updated"
            className="column-last-updated"
            filter="date"
            cells={{ data: dateCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="reviewDateLocal"
            title="Review Date"
            filter="date"
            className="column-review-date"
            cells={{ data: dateCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="publishType"
            title="Type"
            className="column-type"
            cells={{ filterCell: typesFilterCellMemo }}
          />
          <GridColumn
            field="category"
            title="Category"
            className="column-category ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: categoryFilterCell }}
          />
          <GridColumn
            field="publishedTo"
            title="Published To"
            className="column-published-to ellipsis-column"
            filterable={false}
            cells={{ data: ellipsisCell }}
          />
          <GridColumn
            field="status"
            title="Status"
            className="column-status"
            cells={{
              data: statusCell,
              filterCell: statusFilterCell
            }}
          />
          <GridColumn
            title="Actions"
            sortable={false}
            filterable={false}
            resizable={false}
            width={250}
            className="column-actions"
            cells={{
              data: generateActionsCell
            }}
          />
        </KendoReactGrid>
        {viewTagsTarget && (
          <LibraryTagsModal
            title={viewTagsTarget.name}
            onClose={() => setViewTagsTarget(undefined)}
            flowmodelId={viewTagsTarget.id}
          />
        )}
        {componentWarningData && (
          <RunnerSetupDeleteComponentWarningModal
            componentTypeName={componentWarningData.componentTypeName}
            flowModelNames={componentWarningData.flowModelNames}
            onClose={closeComponentWarningModal}
          />
        )}
      </div>
    )) ||
    (gridItemsLoaded && allGridItems.length === 0 && (
      <EmptyState
        summaryText="Build your process Library"
        bodyText={libraryService.emptyStateBody}
        buttonText={canCreateFlowModels ? 'Add a Flow Model' : undefined}
      />
    ))
  );
};

export default LibraryProcessMapsGrid;

export interface ILibraryFlowModelSummary extends IFlowModelSummary {
  lastUpdatedLocal: Date;
  reviewDateLocal?: Date;
  tagIds: Guid[];
  type: string;
}
